import { z } from "zod";
import { EventTimeSourceSchema } from "./event-time-provenance";
import { InductionDisplayDescriptorWireSchema } from "./site-induction";

export const SitePortalRoleSchema = z.enum(["WORKER", "CONTRACTOR_MANAGER"]);
export type SitePortalRole = z.infer<typeof SitePortalRoleSchema>;

export const ContractorPortalCompanySchema = z.object({
  id: z.string(),
  name: z.string(),
});

// #2411 server-boundary variant — see the `*Strict` block at the end of this
// file for why the shared definitions above are permissive.
export const ContractorPortalCompanyStrict =
  ContractorPortalCompanySchema.strict();

export const ContractorPortalSiteDetailSchema = z.object({
  // Stable field identity (issue #991/#1082) — lets clients render
  // per-locale labels. Optional so pre-#1082 payloads stay compatible (the
  // mobile client parses via safeParseOrWarn, which falls back to raw).
  key: z.string().optional(),
  label: z.string(),
  value: z.string().nullable(),
});

// #2411 server-boundary variant.
export const ContractorPortalSiteDetailStrict =
  ContractorPortalSiteDetailSchema.strict();

export type ContractorPortalSiteDetail = z.infer<
  typeof ContractorPortalSiteDetailSchema
>;

/**
 * The mobile app parses these DTOs off the wire via `safeParseOrWarn`, so
 * they are deliberately NOT `.strict()` (#2411): an installed build must
 * tolerate a field the server adds after it shipped. The server's
 * serialisers (`app/api/mobile/v1/portal/_helpers.ts` and the attendance
 * route) build payloads field-by-field and `.parse()` them as an
 * over-serialisation guard, which is where strictness is re-applied — see
 * the `*Strict` variants at the end of this file.
 */
export const ContractorPortalFileSchema = z.object({
  id: z.string(),
  title: z.string(),
  category: z.string(),
  downloadUrl: z.string().url().nullable(),
  requiresAcknowledgement: z.boolean().optional(),
});

export type ContractorPortalFile = z.infer<typeof ContractorPortalFileSchema>;

export const ContractorPortalDocumentSchema = z.object({
  id: z.string(),
  documentType: z.string(),
  title: z.string(),
  versionNumber: z.number().int().nullable(),
  downloadUrl: z.string().url().nullable(),
  // Id of the version whose `downloadUrl`/`versionNumber` are shown here. The
  // mobile RAMS sign-off signs THIS id (the version the worker actually opened),
  // not a separately-fetched "latest", so a RAMS regenerated after this snapshot
  // can't be signed without the worker viewing it. Defaults null where absent.
  documentVersionId: z.string().nullable().default(null),
  // Whether THIS portal user has acknowledged the doc (toolbox talks). Lets the
  // mobile detail screen reflect server state across navigation instead of
  // resetting to "Acknowledge" each time. Defaults false where not populated.
  acknowledged: z.boolean().default(false),
  // True when the owning account runs named-approver document approvals and NO
  // version of this document has been approved for issue yet. The document is
  // still listed — `versionNumber`, `downloadUrl` and `documentVersionId` are
  // then all null, there is no file behind it — and the app labels it
  // "Awaiting approval". Optional so an older server payload still parses;
  // absent is read as false.
  awaitingApproval: z.boolean().optional(),
});

export type ContractorPortalDocument = z.infer<
  typeof ContractorPortalDocumentSchema
>;

export const ContractorPortalProjectSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    siteAddress: z.string().nullable(),
    contractorRole: SitePortalRoleSchema,
    company: ContractorPortalCompanySchema.nullable(),
    checkedIn: z.boolean(),
    /**
     * Server-issued id of the worker's OPEN attendance session, or null.
     * The app persists it with a queued check-out so the replay binds to that
     * exact visit — a device clock can be wrong, an id cannot (#2336).
     * Optional so an older server payload still parses.
     */
    activeSessionId: z.string().nullable().optional(),
    /**
     * Version of that session (`updatedAt`, ISO). Persisted with a queued
     * check-out and re-sent on EVERY attempt, so a 503 retry still compares the
     * version captured at tap time rather than one re-derived server-side after
     * an owner deliberately re-opened the row (#2336).
     */
    activeSessionReopenCount: z.number().int().nullable().optional(),
    siteInductionRequired: z.boolean().default(true),
    siteInductionAcknowledged: z.boolean().default(false),
    // Site Control only: true when an unsigned current RAMS blocks this worker's
    // check-in (mirrors the attendance route's 428). The mobile app gates the
    // check-in button on it AND on cached sign-off state, so an offline check-in
    // can't slip past the server gate. Default false (Business is track-only).
    ramsSignoffBlocksCheckIn: z.boolean().default(false),
    siteFieldsHash: z.string().nullable().optional(),
    inductionDisplayDescriptor: InductionDisplayDescriptorWireSchema.optional(),
    siteDetails: z.array(ContractorPortalSiteDetailSchema),
    files: z.array(ContractorPortalFileSchema),
    documents: z.array(ContractorPortalDocumentSchema),
    pendingAcknowledgements: z.number().int().min(0),
    openRfis: z.number().int().min(0),
    // #1181 (mobile all-plans parity): true when this project is surfaced to the
    // worker for SIGN-OFF ONLY — the account isn't Business+ (or the project's
    // Site Access toggle is off) so the worker signs their mandatory documents
    // but the Site Access *feature* surfaces (attendance/check-in, site files,
    // credentials, RFIs) are NOT available. The server zeroes those fields for
    // such projects; the app hides the feature UI. Default false = full Business
    // Site Access project (back-compat for older app builds).
    signoffOnly: z.boolean().default(false),
  });

export type ContractorPortalProject = z.infer<typeof ContractorPortalProjectSchema>;

export const ContractorPortalProjectResponse = z
  .object({
    project: ContractorPortalProjectSchema,
  })
  .strict();

export type ContractorPortalProjectResponse = z.infer<
  typeof ContractorPortalProjectResponse
>;

export const ContractorPortalProjectListResponse = z
  .object({
    projects: z.array(ContractorPortalProjectSchema),
  })
  .strict();

export type ContractorPortalProjectListResponse = z.infer<
  typeof ContractorPortalProjectListResponse
>;

export const ContractorPortalFilesResponse = z
  .object({
    files: z.array(ContractorPortalFileSchema),
  })
  .strict();

export type ContractorPortalFilesResponse = z.infer<
  typeof ContractorPortalFilesResponse
>;

export const ContractorPortalDocumentsResponse = z
  .object({
    documents: z.array(ContractorPortalDocumentSchema),
  })
  .strict();

export type ContractorPortalDocumentsResponse = z.infer<
  typeof ContractorPortalDocumentsResponse
>;

export const ContractorPortalAttendanceResponse = z.object({
  checkedIn: z.boolean(),
  activeSessionId: z.string().nullable().optional(),
  /** Version of that session — see ContractorPortalProject (#2336). */
  activeSessionReopenCount: z.number().int().nullable().optional(),
});

export type ContractorPortalAttendanceResponse = z.infer<
  typeof ContractorPortalAttendanceResponse
>;

export const ContractorPortalWorkerAttendanceSchema = z.object({
  id: z.string(),
  checkedInAt: z.string().datetime(),
  checkedOutAt: z.string().datetime().nullable(),
  source: z.string().nullable(),
  checkedInReceivedAt: z.string().datetime().nullable().optional(),
  checkedInTimeSource: EventTimeSourceSchema.nullable().optional(),
  checkedOutReceivedAt: z.string().datetime().nullable().optional(),
  checkedOutTimeSource: EventTimeSourceSchema.nullable().optional(),
});

// #2411 server-boundary variant.
export const ContractorPortalWorkerAttendanceStrict =
  ContractorPortalWorkerAttendanceSchema.strict();

export const ContractorPortalWorkerSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  active: z.boolean(),
  role: SitePortalRoleSchema,
  company: ContractorPortalCompanySchema.nullable(),
  checkedIn: z.boolean(),
  latestCheckInAt: z.string().datetime().nullable(),
  latestCheckOutAt: z.string().datetime().nullable(),
  attendanceHistory: z.array(ContractorPortalWorkerAttendanceSchema),
  credentialSummary: z.object({
    pending: z.number().int().min(0),
    approved: z.number().int().min(0),
    rejected: z.number().int().min(0),
    expiringSoon: z.number().int().min(0),
  }),
  pendingAcknowledgements: z.number().int().min(0),
});

export type ContractorPortalWorker = z.infer<
  typeof ContractorPortalWorkerSchema
>;

export const ContractorPortalWorkersResponse = z
  .object({
    workers: z.array(ContractorPortalWorkerSchema),
  })
  .strict();

export type ContractorPortalWorkersResponse = z.infer<
  typeof ContractorPortalWorkersResponse
>;

export const ContractorPortalWorkerResponse = z
  .object({
    worker: ContractorPortalWorkerSchema,
  })
  .strict();

export type ContractorPortalWorkerResponse = z.infer<
  typeof ContractorPortalWorkerResponse
>;

export const PortalCredentialOwnerTypeSchema = z.enum([
  "PORTAL_USER",
  "PORTAL_COMPANY",
]);

export const PortalCredentialTypeSchema = z.enum([
  "CSCS_CARD",
  "INSURANCE",
  "RAMS",
  "METHOD_STATEMENT",
  "H_AND_S_POLICY",
  "TRAINING_CERTIFICATE",
  "OTHER",
]);

export const PortalCredentialStatusSchema = z.enum([
  "REQUESTED",
  "UPLOADED",
  "APPROVED",
  "REJECTED",
  "EXPIRED",
]);

export const PortalCredentialSchema = z.object({
  id: z.string(),
  ownerType: PortalCredentialOwnerTypeSchema.default("PORTAL_USER"),
  type: PortalCredentialTypeSchema,
  status: PortalCredentialStatusSchema,
  title: z.string(),
  cardNumber: z.string().nullable().optional(),
  expiresAt: z.string().datetime().nullable(),
  fileUrl: z.string().url().nullable(),
  reviewNotes: z.string().nullable(),
});

export type PortalCredential = z.infer<typeof PortalCredentialSchema>;

// #2411 server-boundary variants (the record extends the permissive base —
// zod `.extend` would otherwise inherit nothing to re-strict here).
export const PortalCredentialStrict = PortalCredentialSchema.strict();

export const PortalCredentialRecordSchema = PortalCredentialSchema.extend({
  portalUserId: z.string().nullable(),
  portalCompanyId: z.string().nullable(),
  ownerName: z.string().nullable(),
  companyName: z.string().nullable(),
  expiringSoon: z.boolean().default(false),
  // CSCS Smart Check verification (G1). Null on non-CSCS / not-yet-checked.
  verificationProvider: z.enum(["manual", "smartcheck"]).nullable().default(null),
  verificationStatus: z
    .enum(["valid", "expired", "revoked", "fraudulent", "unverified"])
    .nullable()
    .default(null),
  verifiedAt: z.string().datetime().nullable().default(null),
  holderName: z.string().nullable().default(null),
  holderScheme: z.string().nullable().default(null),
  holderOccupation: z.string().nullable().default(null),
});

export const PortalCredentialRecordStrict =
  PortalCredentialRecordSchema.strict();

export type PortalCredentialRecord = z.infer<
  typeof PortalCredentialRecordSchema
>;

export const PortalCredentialsResponse = z
  .object({
    credentials: z.array(PortalCredentialRecordSchema),
  })
  .strict();

export type PortalCredentialsResponse = z.infer<
  typeof PortalCredentialsResponse
>;

export const PortalCredentialResponse = z
  .object({
    credential: PortalCredentialRecordSchema,
  })
  .strict();

export type PortalCredentialResponse = z.infer<
  typeof PortalCredentialResponse
>;

export const DocumentReviewAssignmentStatusSchema = z.enum([
  "PENDING",
  "ACKNOWLEDGED",
  "REJECTED",
  "SUPERSEDED",
]);

export const DocumentReviewAssignmentSchema = z.object({
  id: z.string(),
  documentId: z.string().nullable(),
  documentVersionId: z.string().nullable(),
  siteFileId: z.string().nullable(),
  assigneeUserId: z.string().nullable(),
  assigneePortalUserId: z.string().nullable(),
  status: DocumentReviewAssignmentStatusSchema,
  note: z.string().nullable(),
  responseNote: z.string().nullable(),
  requestedAt: z.string().datetime(),
  respondedAt: z.string().datetime().nullable(),
  targetTitle: z.string(),
  downloadUrl: z.string().url().nullable(),
});

// #2411 server-boundary variant.
export const DocumentReviewAssignmentStrict =
  DocumentReviewAssignmentSchema.strict();

export type DocumentReviewAssignment = z.infer<
  typeof DocumentReviewAssignmentSchema
>;

export const DocumentReviewAssignmentsResponse = z
  .object({
    assignments: z.array(DocumentReviewAssignmentSchema),
  })
  .strict();

export type DocumentReviewAssignmentsResponse = z.infer<
  typeof DocumentReviewAssignmentsResponse
>;

export const DocumentReviewAssignmentResponse = z
  .object({
    assignment: DocumentReviewAssignmentSchema,
  })
  .strict();

export type DocumentReviewAssignmentResponse = z.infer<
  typeof DocumentReviewAssignmentResponse
>;

// ─── Server-only strict variants (#2411 batches 1 + non-plant) ───────────────

/**
 * Full-depth strict rebuilds of the de-stricted portal response DTOs above,
 * for the server's own response boundaries (`_helpers.ts` serialisers and
 * the attendance route). The mobile app parses the non-strict bases, so a
 * field the server adds must not fail an installed build; the server still
 * wants the over-serialisation guard, so it parses what it serialises
 * through these instead (same pattern as `ToolboxLogResponseStrict`).
 *
 * `.strict()` only applies to the object it is called on, so every shared
 * child (`company`, `siteDetails`, `attendanceHistory`, the credential
 * summary, `files`, `documents`) is re-stated at its strict form here — a
 * bare `X.strict()` on the root would leave each child exactly as
 * permissive as the base the mobile app parses.
 */
export const ContractorPortalFileStrict = ContractorPortalFileSchema.strict();

export const ContractorPortalDocumentStrict =
  ContractorPortalDocumentSchema.strict();

export const ContractorPortalProjectStrict =
  ContractorPortalProjectSchema.extend({
    company: ContractorPortalCompanyStrict.nullable(),
    siteDetails: z.array(ContractorPortalSiteDetailStrict),
    files: z.array(ContractorPortalFileStrict),
    documents: z.array(ContractorPortalDocumentStrict),
  }).strict();

export const ContractorPortalAttendanceResponseStrict =
  ContractorPortalAttendanceResponse.strict();

export const ContractorPortalWorkerStrict =
  ContractorPortalWorkerSchema.extend({
    company: ContractorPortalCompanyStrict.nullable(),
    attendanceHistory: z.array(ContractorPortalWorkerAttendanceStrict),
    credentialSummary: ContractorPortalWorkerSchema.shape.credentialSummary.strict(),
  }).strict();

export const ContractorPortalProjectResponseStrict = z
  .object({ project: ContractorPortalProjectStrict })
  .strict();

export const ContractorPortalProjectListResponseStrict = z
  .object({ projects: z.array(ContractorPortalProjectStrict) })
  .strict();

export const ContractorPortalFilesResponseStrict = z
  .object({ files: z.array(ContractorPortalFileStrict) })
  .strict();

export const ContractorPortalDocumentsResponseStrict = z
  .object({ documents: z.array(ContractorPortalDocumentStrict) })
  .strict();

export const ContractorPortalWorkersResponseStrict = z
  .object({ workers: z.array(ContractorPortalWorkerStrict) })
  .strict();

export const ContractorPortalWorkerResponseStrict = z
  .object({ worker: ContractorPortalWorkerStrict })
  .strict();

export const PortalCredentialsResponseStrict = z
  .object({ credentials: z.array(PortalCredentialRecordStrict) })
  .strict();

export const PortalCredentialResponseStrict = z
  .object({ credential: PortalCredentialRecordStrict })
  .strict();

export const DocumentReviewAssignmentsResponseStrict = z
  .object({ assignments: z.array(DocumentReviewAssignmentStrict) })
  .strict();

export const DocumentReviewAssignmentResponseStrict = z
  .object({ assignment: DocumentReviewAssignmentStrict })
  .strict();
